/*
 * sc-collection-dots-fix.js  (Horizon 4.1.x)
 * ---------------------------------------------------------------------------
 * WIRED IN via a <script defer> tag near the end of layout/theme.liquid.
 *
 * What it fixes on this theme:
 *   1. POSITION - ScaleChart's "Colour dots (under the card)" row is injected
 *      as a direct child of <product-card>, which is display:grid. The stray
 *      grid item auto-places into the first row, so the dots render ABOVE the
 *      product image. This script relocates the row to the end of
 *      `.product-card__content` (a flex column), so it sits BELOW the title
 *      and price, matching the sister (Pipeline) theme and the app's own
 *      admin preview.
 *   2. APPEARANCE - with the shop's PDP layout set to "swatch"/image, the app
 *      paints each collection dot as the variant PHOTO. The merchant wants
 *      solid colour dots for this style (same call that was made on the
 *      Pipeline theme). We repaint every dot from its colour name using the
 *      map below, with a CSS.supports() fallback and, last resort, a grey dot
 *      showing the colour's initial. Image-carousel rows (collectionSwatch-
 *      Style "thumbnails", children .sc-cc-thumb) are ONLY relocated, never
 *      recoloured - otherwise the merchant's chosen photos turn into blocks.
 *
 * Horizon-specific bits:
 *   - CARD_SEL / INFO_SEL point at Horizon's <product-card> / .product-card__content.
 *   - The relocated row gets position:relative + z-index so it stays clickable
 *     above Horizon's full-card <a class="product-card__link"> overlay.
 *   - <html> gets .sc-cdf-ready so the injected CSS can hide the un-relocated
 *     row until we move it (no top-of-card flash). The hide rule is scoped to
 *     that class, so if this script never runs the dots still show (just in
 *     the old position) rather than vanishing.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  var ROW_SEL = '[data-sc-coll-dots]';
  var CARD_SEL = 'product-card, .product-card, .card-wrapper, .grid__item, li.product-grid__item';
  var INFO_SEL = '.product-card__content';

  document.documentElement.classList.add('sc-cdf-ready');

  // One-time CSS: hide the un-fixed row, and pin the relocated row above the
  // card's overlay link. Injected (not in a .css asset) so the whole fix is
  // one file / one <script> tag.
  (function injectCSS() {
    if (document.getElementById('sc-cdf-css')) return;
    var st = document.createElement('style');
    st.id = 'sc-cdf-css';
    st.textContent =
      'html.sc-cdf-ready [data-sc-coll-dots]:not(.sc-coll-dots-fixed),' +
      'html.sc-cdf-ready .sc-row-scroll-host:not([data-sc-moved]) [data-sc-coll-dots]:not(.sc-coll-dots-fixed){visibility:hidden}' +
      '.product-card__content > [data-sc-moved],' +
      '.product-card__content > .sc-cdf-dots,' +
      '.product-card__content > .sc-cdf-carousel{position:relative;z-index:2;width:100%;margin:8px 0 2px;order:99}';
    (document.head || document.documentElement).appendChild(st);
  })();

  var MAP = {
    burgundy: '#5c1a2b', wine: '#722f37', bordeaux: '#5b1a1a', merlot: '#63181b',
    maroon: '#7b1f2b', oxblood: '#4a1c1c', claret: '#6e1b2a',
    cream: '#f4ecd8', ivory: '#f6f2e6', ecru: '#d9cfb8', oatmeal: '#ded4c0',
    offwhite: '#f3f1ea', natural: '#e8dfca', bone: '#e5dccb', champagne: '#efd9b4',
    charcoal: '#36393b', slate: '#5a6068', graphite: '#3a3d40', gunmetal: '#3a3f43',
    anthracite: '#333537', ash: '#b2b0a9',
    camel: '#c19a6b', tan: '#d2b48c', taupe: '#a89a8b', mushroom: '#bcaea0',
    sand: '#dcc9a6', stone: '#cabfa8', biscuit: '#e0c9a6', latte: '#c8a97e',
    cognac: '#9a463d', caramel: '#a9682f', toffee: '#8a5a2b', chocolate: '#4e342e',
    coffee: '#4b3621', mocha: '#6f5643', espresso: '#3c2f2f', walnut: '#5c4033',
    rust: '#a1441f', terracotta: '#b5502d', brick: '#8f3a2d', copper: '#a75b32',
    ginger: '#b06a3b', pumpkin: '#c15a1e', amber: '#c98a1a',
    mustard: '#d4a017', ochre: '#c48f2a', honey: '#dba43b', marigold: '#e6a817',
    olivegreen: '#6b6b3a', army: '#5a5a3c', military: '#575c3c', khakigreen: '#7a7550',
    khaki: '#7a7550', olive: '#6b6b3a',
    sage: '#9caf88', moss: '#697a55', fern: '#5b7a4a', forest: '#274029',
    hunter: '#2f4a35', pine: '#2e4a3d', emerald: '#1f7a5c', kelly: '#2d9a52',
    bottle: '#26443a', jade: '#3aa17e', mint: '#bfe3cf',
    petrol: '#1f4e5a', denim: '#3b5b7d', airforce: '#4a6a86',
    cobalt: '#1c3faa', royal: '#2540a8', royalblue: '#2540a8', ink: '#1f2a44',
    navy: '#1b2a4a', navyblue: '#1b2a4a', midnight: '#1a2238', marine: '#1f3a5f',
    powderblue: '#c4d8e8', skyblue: '#8fc1e3', babyblue: '#bcd8ee', iceblue: '#dae8ef',
    aqua: '#4cc7d0', teal: '#1c7a80',
    lilac: '#c8a2d6', lavendergrey: '#b9b3c8', mauve: '#a879a0', heather: '#a99bb0',
    plum: '#6a3560', aubergine: '#4b2a4a', eggplant: '#4b2a4a', grape: '#5a2a6a',
    orchid: '#b56bb0', violet: '#7f4fbf', amethyst: '#7a4fa3',
    blush: '#e7c1c4', rose: '#e2a4ad', dustyrose: '#c98f97', dustypink: '#d6a3ab',
    fuchsia: '#d21f7a', magenta: '#c81f7a', hotpink: '#e84a9c', bubblegum: '#f19bc4',
    coralpink: '#f2977f', peach: '#f6c9a8', apricot: '#f2b57e', melon: '#f4a582',
    nude: '#e5c2a8', flesh: '#e6c0a5',
    lemon: '#f2df6e', canary: '#f3e04a', butter: '#f3e2a1',
    lime: '#b7d94c', chartreuse: '#c4d63a', pistachio: '#c3d59a',
    burntorange: '#c1521d', tangerine: '#e2762a',
    scarlet: '#c62b2b', cherry: '#a41f2b', ruby: '#9b1b30',
    poppy: '#d7332b', tomato: '#d74c37', chilli: '#b32d24',
    greymarl: '#b8b8b8', greymarle: '#b8b8b8', marl: '#c1c1c1', marlgrey: '#b8b8b8',
    heathergrey: '#b7b7bb', greymelange: '#b6b6b6', melange: '#bdbdbd',
    lightgrey: '#d3d3d3', darkgrey: '#5a5a5a', midgrey: '#9a9a9a',
    nickel: '#8f9194', pewter: '#8b8c89', pearl: '#eae7df', platinum: '#d9d7d0',
    black: '#111111', white: '#f6f6f6', grey: '#8f8f8f', gray: '#8f8f8f',
    // German colour names (this shop's storefront language is de)
    schwarz: '#111111', weiss: '#f6f6f6', grau: '#8f8f8f', blau: '#3b5b7d',
    rot: '#c62b2b', gruen: '#2f4a35', gelb: '#f2df6e', braun: '#5c4033',
    beige: '#e0c9a6', rosa: '#e2a4ad', lila: '#7f4fbf', orange: '#c15a1e',
    dunkelblau: '#1b2a4a', hellblau: '#8fc1e3', dunkelgrau: '#5a5a5a', hellgrau: '#d3d3d3',
    // Danish / Norwegian colour names (this catalogue has Nordic source data,
    // e.g. the mistyped "Lsyerød" = Lyserød = pink)
    sort: '#111111', hvid: '#f6f6f6', gra: '#8f8f8f', graa: '#8f8f8f',
    rod: '#c62b2b', roed: '#c62b2b', bla: '#3b5b7d', blaa: '#3b5b7d',
    gron: '#2f4a35', groen: '#2f4a35', gul: '#f2df6e', brun: '#5c4033',
    lilla: '#7f4fbf', lyserod: '#e2a4ad', lyseroed: '#e2a4ad', lsyerod: '#e2a4ad',
    morkeblaa: '#1b2a4a', morkebla: '#1b2a4a', lyseblaa: '#8fc1e3', lysebla: '#8fc1e3',
    marineblaa: '#1f3a5f', lysegron: '#9caf88', lysegroen: '#9caf88'
  };

  // A dot label that is really a SIZE (S / M / L / XL / 2XL / numeric / "One Size"
  // / a range like S-M). These get dropped from the row entirely - a colour is
  // never literally "L", so this only ever removes mis-classified values.
  var SIZE_RE = /^(?:(?:xxs|xs|s|m|l|xl|xxl|xxxl|[2-6]xl|[2-6]xs)(?:[\s/&+-]+(?:xxs|xs|s|m|l|xl|xxl|xxxl|[2-6]xl))*|(?:uk|eu|us|au|w|d)?\s?\d{1,3}(?:[.,]\d)?|small|medium|large|x-?large|xx-?large|xxx-?large|extra[\s-]?small|extra[\s-]?large|klein|mittel|gross|one[\s-]?size|onesize|o\/?s|free[\s-]?size|einheitsgr(?:o|ö)sse|taille[\s-]?unique|str(?:\.|\s)?\d{1,3})$/i;

  var MULTI_RE = /multi|rainbow|assort|colou?rful|tie.?dye|camo|floral|pattern|print|mix(ed)?|stripe|check(ed)?|plaid|paisley|bunt|gemustert/i;
  var MULTI_BG = 'linear-gradient(135deg,#f6d365 0%,#fda085 34%,#ee9ca7 67%,#a18cd1 100%)';
  // Unresolved colour: a neutral hatched chip - NEVER a letter/initial (a bare
  // letter reads as a size, which is exactly what the merchant flagged).
  var UNKNOWN_BG = 'repeating-linear-gradient(45deg,#ececec,#ececec 3px,#dcdcdc 3px,#dcdcdc 6px)';

  var _supportCache = {};
  function cssColorOK(v) {
    if (!v) return false;
    if (v in _supportCache) return _supportCache[v];
    var ok = false;
    try { ok = window.CSS && CSS.supports && CSS.supports('color', v); } catch (e) { ok = false; }
    _supportCache[v] = ok;
    return ok;
  }

  function ascii(str) {
    return (str || '')
      .toLowerCase()
      .replace(/ä/g, 'a').replace(/ö/g, 'o').replace(/ü/g, 'u').replace(/ß/g, 'ss')
      .replace(/ø/g, 'o').replace(/æ/g, 'ae').replace(/å/g, 'a')
      .replace(/[àáâãå]/g, 'a').replace(/[èéêë]/g, 'e').replace(/[ìíîï]/g, 'i')
      .replace(/[òóôõ]/g, 'o').replace(/[ùúû]/g, 'u').replace(/ç/g, 'c').replace(/ñ/g, 'n');
  }

  function resolveColor(raw) {
    var val = (raw || '').trim();
    if (!val) return null;
    if (MULTI_RE.test(val)) return MULTI_BG;

    var norm = ascii(val).replace(/[^a-z]/g, '');
    if (!norm) return null;

    if (MAP[norm]) return MAP[norm];
    if (cssColorOK(norm)) return norm;

    var despaced = ascii(val).replace(/[^a-z-]/g, '');
    if (cssColorOK(despaced)) return despaced;

    var tokens = ascii(val).split(/[^a-z]+/).filter(Boolean);
    for (var i = tokens.length - 1; i >= 0; i--) {
      var t = tokens[i];
      if (MAP[t]) return MAP[t];
      if (cssColorOK(t)) return t;
    }

    var keys = Object.keys(MAP).sort(function (a, b) { return b.length - a.length; });
    for (var k = 0; k < keys.length; k++) {
      if (keys[k].length >= 4 && norm.indexOf(keys[k]) !== -1) return MAP[keys[k]];
    }
    return null;
  }

  function isSizeLabel(val) {
    var t = (val || '').trim();
    if (!t) return false;
    return SIZE_RE.test(t) || SIZE_RE.test(ascii(t).replace(/[^a-z0-9/&\s-]/g, ''));
  }

  // Returns 'removed' if the dot was a size (taken out of the row), else 'kept'.
  function recolorDot(dot) {
    if (!dot || dot.nodeType !== 1) return 'kept';
    var val = dot.getAttribute('title') || dot.getAttribute('aria-label') || '';

    // A size value that leaked into the colour option - drop it, don't paint it.
    if (isSizeLabel(val)) {
      if (dot.parentNode) dot.parentNode.removeChild(dot);
      return 'removed';
    }

    if (dot.getAttribute('data-sc-recolored') === '1') return 'kept';

    var color = resolveColor(val);
    var inner = dot.querySelector('i, img');
    if (inner) inner.parentNode.removeChild(inner);

    dot.textContent = '';                       // never any text inside a dot
    dot.style.setProperty('background-image', 'none', 'important');

    if (color) {
      dot.style.setProperty('background', color, 'important');
      dot.style.setProperty('background-size', 'auto', 'important');
    } else {
      // Unknown colour name -> neutral hatched chip (no letter).
      dot.style.setProperty('background', UNKNOWN_BG, 'important');
      dot.style.setProperty('background-size', 'auto', 'important');
    }
    dot.setAttribute('data-sc-recolored', '1');
    return 'kept';
  }

  function relocateRow(row) {
    var card = row.closest(CARD_SEL);
    if (!card) return;
    var info = card.querySelector(INFO_SEL) || card;

    // Move the scroll host (row + prev/next arrows) when present, else the row.
    var wrapper = row;
    var p = row.parentElement;
    if (p && p !== info && p.classList && p.classList.contains('sc-row-scroll-host')) {
      wrapper = p;
    }
    // Already the last child of the info panel -> nothing to do (idempotent,
    // keeps the MutationObserver from ping-ponging).
    if (wrapper.parentElement === info && info.lastElementChild === wrapper) return;

    wrapper.setAttribute('data-sc-moved', '1');
    // Stay above Horizon's absolutely-positioned full-card link overlay.
    wrapper.style.position = 'relative';
    wrapper.style.zIndex = '2';
    info.appendChild(wrapper);
  }

  function processRow(row) {
    if (!row || row.nodeType !== 1) return;

    var isCarousel = !!row.querySelector('.sc-cc-thumb');
    row.classList.add(isCarousel ? 'sc-cdf-carousel' : 'sc-cdf-dots');

    if (!isCarousel) {
      var dots = row.querySelectorAll('span[title], span[aria-label]');
      var kept = 0;
      for (var i = 0; i < dots.length; i++) {
        if (recolorDot(dots[i]) === 'kept') kept++;
      }
      // Every value was a size (mis-tagged option) -> nothing to show.
      if (kept === 0) { row.style.display = 'none'; row.classList.add('sc-coll-dots-fixed'); return; }
    }
    relocateRow(row);
    row.classList.add('sc-coll-dots-fixed');
  }

  var _scheduled = false;
  function processAll() {
    _scheduled = false;
    var rows = document.querySelectorAll(ROW_SEL);
    for (var i = 0; i < rows.length; i++) processRow(rows[i]);
  }
  function schedule() {
    if (_scheduled) return;
    _scheduled = true;
    (window.requestAnimationFrame || window.setTimeout)(processAll, 16);
  }

  function nodeHasRow(n) {
    return n.nodeType === 1 &&
      (n.matches && n.matches(ROW_SEL) ||
       n.querySelector && n.querySelector(ROW_SEL));
  }

  function init() {
    processAll();

    var obs = new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        var added = muts[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          if (nodeHasRow(added[j])) { schedule(); return; }
        }
      }
    });
    obs.observe(document.body, { childList: true, subtree: true });

    [250, 800, 2000, 4000].forEach(function (t) { setTimeout(processAll, t); });
    document.addEventListener('shopify:section:load', function () { setTimeout(processAll, 60); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
